import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ColorPicker } from "@/components/ui/color-picker";
import { 
  Undo2, 
  Redo2, 
  Save, 
  Palette,
  Minus,
  Plus,
  Type,
  X,
  Square,
  Circle,
  Star,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ReactSketchCanvas } from "react-sketch-canvas";
import type { ReactSketchCanvasRef } from "react-sketch-canvas";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TextAnnotationManager, type TextElement } from "./ImageEditor/TextAnnotationManager";
// ShapeAnnotationManager can be easily removed if not needed
import { ShapeAnnotationManager, type ShapeElement, type ShapeType } from "./ImageEditor/ShapeAnnotationManager";

interface ImageEditorProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  onSave?: (editedImageBlob: Blob) => void;
}

export default function ImageEditor({ isOpen, onClose, imageSrc, onSave }: ImageEditorProps) {
  const { t } = useTranslation('components.imageEditor');
  const canvasRef = useRef<ReactSketchCanvasRef>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const textCanvasRef = useRef<HTMLCanvasElement>(null);
  const textManagerRef = useRef<TextAnnotationManager | null>(null);
  // ShapeAnnotationManager can be easily removed if not needed
  const shapeCanvasRef = useRef<HTMLCanvasElement>(null);
  const shapeManagerRef = useRef<ShapeAnnotationManager | null>(null);
  // Fallback local drawing state for shapes (in case manager isn't active)
  const shapeDrawRef = useRef<{ active: boolean; startX: number; startY: number; lastX: number; lastY: number; type: 'rectangle'|'circle'|'star'|null }>(
    { active: false, startX: 0, startY: 0, lastX: 0, lastY: 0, type: null }
  );
  
  const [strokeColor, setStrokeColor] = useState('#ff0000');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [currentTool, setCurrentTool] = useState<'draw' | 'text' | 'select' | 'rectangle' | 'circle' | 'star'>('draw');
  const [fontSize, setFontSize] = useState(16);
  const [isTextInputOpen, setIsTextInputOpen] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
  const [textElements, setTextElements] = useState<TextElement[]>([]);
  // Shape elements state - can be easily removed if not needed
  const [shapeElements, setShapeElements] = useState<ShapeElement[]>([]);
  
  const { toast } = useToast();
  const currentToolHelpText = t(`toolHelp.${currentTool}`);

  // Initialize text manager
  useEffect(() => {
    if (!textManagerRef.current) {
      textManagerRef.current = new TextAnnotationManager();
    }
  }, []);

  // Initialize shape manager - can be easily removed if not needed
  useEffect(() => {
    if (!shapeManagerRef.current) {
      shapeManagerRef.current = new ShapeAnnotationManager();
    }
  }, []);

  const shapeMgrReadyRef = useRef(false);
  // Ensure shape manager initializes when canvas is ready and size known
  useEffect(() => {
    if (!isOpen) return;
    const mgr = shapeManagerRef.current;
    const shapeCanvas = shapeCanvasRef.current;
    const imgEl = imageRef.current;
    if (mgr && shapeCanvas && imgEl && canvasSize.width > 0 && canvasSize.height > 0) {
      try {
        mgr.initialize(shapeCanvas, imgEl, { width: canvasSize.width, height: canvasSize.height });
        shapeMgrReadyRef.current = true;
        // eslint-disable-next-line no-console
        console.log('[ImageEditor] Shape manager initialized (late)');
      } catch {}
    }
  }, [isOpen, canvasSize.width, canvasSize.height]);

  // Keep manager tool settings in sync with currentTool
  useEffect(() => {
    const mgr = shapeManagerRef.current;
    if (!mgr) return;
    const type = (currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star') ? currentTool : null;
    try { mgr.setToolSettings(type as any, strokeColor, strokeWidth); } catch {}
  }, [currentTool, strokeColor, strokeWidth]);

  // Helpers for fallback drawing directly on the shape canvas
  const getShapeCtx = () => shapeCanvasRef.current?.getContext('2d') || null;
  const clearShapeOverlay = () => {
    const ctx = getShapeCtx();
    if (!ctx || !shapeCanvasRef.current) return;
    ctx.clearRect(0, 0, shapeCanvasRef.current.width, shapeCanvasRef.current.height);
  };
  const drawStarPath = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) => {
    const cx = x + w / 2; const cy = y + h / 2;
    const outer = Math.min(Math.abs(w), Math.abs(h)) / 2;
    const inner = outer * 0.4; const spikes = 5;
    let rot = Math.PI / 2 * 3; let sx = cx; let sy = cy;
    ctx.beginPath(); ctx.moveTo(cx, cy - outer);
    const step = Math.PI / spikes;
    for (let i = 0; i < spikes; i++) {
      sx = cx + Math.cos(rot) * outer; sy = cy + Math.sin(rot) * outer; ctx.lineTo(sx, sy); rot += step;
      sx = cx + Math.cos(rot) * inner; sy = cy + Math.sin(rot) * inner; ctx.lineTo(sx, sy); rot += step;
    }
    ctx.closePath();
  };
  const drawTempShape = (solid: boolean) => {
    const ctx = getShapeCtx();
    if (!ctx) return;
    const { startX, startY, lastX, lastY, type } = shapeDrawRef.current;
    const x = Math.min(startX, lastX); const y = Math.min(startY, lastY);
    const w = Math.abs(lastX - startX); const h = Math.abs(lastY - startY);
    if (!w || !h || !type) return;
    ctx.save();
    ctx.strokeStyle = strokeColor; ctx.lineWidth = strokeWidth; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (!solid) ctx.setLineDash([5, 4]);
    if (type === 'rectangle') {
      ctx.strokeRect(x, y, w, h);
    } else if (type === 'circle') {
      const cx = x + w / 2; const cy = y + h / 2; ctx.beginPath(); ctx.ellipse(cx, cy, w/2, h/2, 0, 0, Math.PI*2); ctx.stroke();
    } else if (type === 'star') {
      drawStarPath(ctx, x, y, w, h); ctx.stroke();
    }
    ctx.restore();
  };

  // Update canvas size when image loads
  useEffect(() => {
    if (isOpen && imageRef.current && imageSrc && textCanvasRef.current && textManagerRef.current) {
      const imageElement = imageRef.current;
      
      const updateCanvasSize = () => {
        if (imageElement.complete && imageElement.naturalWidth) {
          requestAnimationFrame(() => {
            const rect = imageElement.getBoundingClientRect();
            const displayedWidth = rect.width || imageElement.offsetWidth || imageElement.naturalWidth;
            const displayedHeight = rect.height || imageElement.offsetHeight || imageElement.naturalHeight;
            
            if (displayedWidth > 0 && displayedHeight > 0) {
              const newSize = { width: displayedWidth, height: displayedHeight };
              setCanvasSize(newSize);
              
              // Update text manager
              if (textManagerRef.current && textCanvasRef.current) {
                textManagerRef.current.initialize(textCanvasRef.current, imageElement, newSize);
                setTextElements(textManagerRef.current.getTextElements());
              }

              // Update shape manager - can be easily removed if not needed
              if (shapeManagerRef.current && shapeCanvasRef.current) {
                shapeManagerRef.current.initialize(shapeCanvasRef.current, imageElement, newSize);
                setShapeElements(shapeManagerRef.current.getShapes());
              }
            } else {
              setTimeout(updateCanvasSize, 100);
            }
          });
        }
      };

      if (imageElement.complete) {
        updateCanvasSize();
      } else {
        imageElement.onload = updateCanvasSize;
      }

      const handleResize = () => {
        if (imageElement.complete) {
          updateCanvasSize();
        }
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, imageSrc]);

  // Update text elements when text manager changes
  useEffect(() => {
    if (textManagerRef.current) {
      setTextElements(textManagerRef.current.getTextElements());
    }
  }, [textElements.length]);

  // Update shape elements when shape manager changes - can be easily removed if not needed
  useEffect(() => {
    if (shapeManagerRef.current) {
      setShapeElements(shapeManagerRef.current.getShapes());
    }
  }, [shapeElements.length]);

  // Update shape manager tool settings when tool, color, or strokeWidth changes - can be easily removed if not needed
  useEffect(() => {
    if (shapeManagerRef.current) {
      const shapeType = (currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star') 
        ? currentTool 
        : null;
      shapeManagerRef.current.setToolSettings(shapeType, strokeColor, strokeWidth);
    }
  }, [currentTool, strokeColor, strokeWidth]);

  // Handle keyboard shortcuts for shape deletion - can be easily removed if not needed
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && shapeManagerRef.current) {
        const shapes = shapeManagerRef.current.getShapes();
        const selectedShape = shapes.find(s => s.selected);
        if (selectedShape) {
          shapeManagerRef.current.removeShape(selectedShape.id);
          setShapeElements(shapeManagerRef.current.getShapes());
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleCanvasClick = (e: React.MouseEvent) => {
    // Only handle text tool clicks here
    // Shape tools are handled by ShapeAnnotationManager's own event listeners
    if (currentTool !== 'text' || !imageRef.current || !textManagerRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const coords = textManagerRef.current.getImageCoordinates(e.clientX, e.clientY, rect);
    setTextPosition(coords);
    setIsTextInputOpen(true);
    setTextInput('');
  };

  const handleTextSubmit = () => {
    if (!textInput.trim() || !textManagerRef.current) return;

    textManagerRef.current.addText(
      textPosition.x,
      textPosition.y,
      textInput.trim(),
      strokeColor,
      fontSize
    );
    
    setTextElements(textManagerRef.current.getTextElements());
    setIsTextInputOpen(false);
    setTextInput('');
  };

  const handleDeleteText = (id: string) => {
    if (!textManagerRef.current) return;
    
    textManagerRef.current.removeText(id);
    setTextElements(textManagerRef.current.getTextElements());
  };

  // Handle shape deletion - can be easily removed if not needed
  const handleDeleteShape = (id: string) => {
    if (!shapeManagerRef.current) return;
    
    shapeManagerRef.current.removeShape(id);
    setShapeElements(shapeManagerRef.current.getShapes());
  };

  const handleUndo = () => {
    if (currentTool === 'draw') {
      canvasRef.current?.undo();
    } else if (currentTool === 'text' && textManagerRef.current) {
      // Undo last text element
      const elements = textManagerRef.current.getTextElements();
      if (elements.length > 0) {
        textManagerRef.current.removeText(elements[elements.length - 1].id);
        setTextElements(textManagerRef.current.getTextElements());
      }
    } else if ((currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star') && shapeManagerRef.current) {
      // Undo last shape element
      const elements = shapeManagerRef.current.getShapes();
      if (elements.length > 0) {
        shapeManagerRef.current.removeShape(elements[elements.length - 1].id);
        setShapeElements(shapeManagerRef.current.getShapes());
      }
    }
  };

  const handleRedo = () => {
    if (currentTool === 'draw') {
      canvasRef.current?.redo();
    }
    // Text redo not implemented - would need history stack
  };

  const handleClear = () => {
    if (currentTool === 'draw') {
      canvasRef.current?.clearCanvas();
    } else if (currentTool === 'text' && textManagerRef.current) {
      textManagerRef.current.clear();
      setTextElements([]);
    } else if ((currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star') && shapeManagerRef.current) {
      shapeManagerRef.current.clear();
      setShapeElements([]);
    }
  };

  const handleSave = async () => {
    if (!canvasRef.current || !imageRef.current) return;

    try {
      // Export canvas drawing as base64
      const canvasData = await canvasRef.current.exportImage('png');
      
      // Create a new canvas to combine image, drawing, and text
      const combinedCanvas = document.createElement('canvas');
      const imageElement = imageRef.current;
      
      combinedCanvas.width = imageElement.naturalWidth;
      combinedCanvas.height = imageElement.naturalHeight;
      const ctx = combinedCanvas.getContext('2d');
      
      if (!ctx) return;

      // Draw original image
      ctx.drawImage(imageElement, 0, 0);

      // Load and draw the canvas drawing, scaled to match original image size
      const drawingImg = new Image();
      drawingImg.crossOrigin = 'anonymous';
      
      drawingImg.onload = () => {
        const scaleX = imageElement.naturalWidth / canvasSize.width;
        const scaleY = imageElement.naturalHeight / canvasSize.height;
        
        // Draw sketch canvas
        ctx.save();
        ctx.scale(scaleX, scaleY);
        ctx.drawImage(drawingImg, 0, 0);
        ctx.restore();

        // Draw text elements scaled to original image size
        if (textManagerRef.current) {
          textManagerRef.current.drawOnCanvas(ctx, scaleX, scaleY);
        }

        // Draw shape elements: prefer manager; also draw fallback overlay if any
        if (shapeManagerRef.current) {
          shapeManagerRef.current.drawOnCanvas(ctx, scaleX, scaleY);
        }
        // Fallback: if manager not active, composite the shape overlay directly
        if (shapeCanvasRef.current) {
          try {
            ctx.save();
            ctx.scale(scaleX, scaleY);
            ctx.drawImage(shapeCanvasRef.current, 0, 0);
            ctx.restore();
          } catch {}
        }

        // Convert to blob
        combinedCanvas.toBlob((blob) => {
          if (blob && onSave) {
            onSave(blob);
            toast({
              title: t('toasts.saveSuccess.title'),
              description: t('toasts.saveSuccess.description'),
            });
            onClose();
          }
        }, 'image/png');
      };
      
      drawingImg.onerror = () => {
        toast({
          title: t('toasts.saveError.title'),
          description: t('toasts.saveError.description'),
          variant: "destructive",
        });
      };
      
      drawingImg.src = canvasData;
    } catch (error) {
      console.error('Error saving image:', error);
      toast({
        title: t('toasts.saveError.title'),
        description: t('toasts.saveError.description'),
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  const getToolHelpText = (tool: 'draw' | 'text' | 'select' | 'rectangle' | 'circle' | 'star') =>
    t(`toolHelp.${tool}`);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] p-4">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-sm">{t('title')}</DialogTitle>
          <DialogDescription>
            {t('description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4 h-[calc(90vh-120px)]">
          {/* Toolbar */}
          <div className="flex flex-col gap-2 p-3 bg-slate-50 rounded-lg min-w-[180px]">
            {/* Tools */}
            <div className="space-y-2">
              <TooltipProvider delayDuration={0} skipDelayDuration={0}>
                <div className="grid grid-cols-2 gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={currentTool === 'draw' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentTool('draw')}
                        className="h-8 text-xs"
                      >
                        {t('draw')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[240px] text-xs">
                      {getToolHelpText('draw')}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={currentTool === 'text' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentTool('text')}
                        className="h-8 text-xs"
                      >
                        <Type className="h-3 w-3 mr-1" />
                        {t('text')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[240px] text-xs">
                      {getToolHelpText('text')}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={currentTool === 'select' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentTool('select')}
                        className="h-8 text-xs"
                      >
                        {t('select') || 'Select'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[240px] text-xs">
                      {getToolHelpText('select')}
                    </TooltipContent>
                  </Tooltip>
                  {/* Shape tools - can be easily removed if not needed */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={currentTool === 'rectangle' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentTool('rectangle')}
                        className="h-8 text-xs"
                      >
                        <Square className="h-3 w-3 mr-1" />
                        {t('rectangle')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[240px] text-xs">
                      {getToolHelpText('rectangle')}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={currentTool === 'circle' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentTool('circle')}
                        className="h-8 text-xs"
                      >
                        <Circle className="h-3 w-3 mr-1" />
                        {t('circle')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[240px] text-xs">
                      {getToolHelpText('circle')}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={currentTool === 'star' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentTool('star')}
                        className="h-8 text-xs col-span-2"
                      >
                        <Star className="h-3 w-3 mr-1" />
                        {t('star')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[240px] text-xs">
                      {getToolHelpText('star')}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </div>

            <Separator />

            {/* Brush Size / Font Size / Stroke Width */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">
                {currentTool === 'draw' ? t('brushSize') : 
                 currentTool === 'text' ? t('textSize') : 
                 t('strokeWidth')}
              </Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (currentTool === 'draw' || currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star') {
                      setStrokeWidth(Math.max(1, strokeWidth - 1));
                    } else {
                      setFontSize(Math.max(8, fontSize - 2));
                    }
                  }}
                  className="h-6 w-6 p-0"
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="text-xs font-medium min-w-[20px] text-center">
                  {currentTool === 'draw' || currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star' ? strokeWidth : fontSize}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (currentTool === 'draw' || currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star') {
                      setStrokeWidth(Math.min(20, strokeWidth + 1));
                    } else {
                      setFontSize(Math.min(48, fontSize + 2));
                    }
                  }}
                  className="h-6 w-6 p-0"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <Separator />

            {/* Colors */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('color')}</Label>
              <ColorPicker
                value={strokeColor}
                onChange={setStrokeColor}
              />
            </div>

            <Separator />

            {/* Actions */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('actions')}</Label>
              <div className="grid grid-cols-2 gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUndo}
                  className="h-8 text-xs"
                >
                  <Undo2 className="h-3 w-3" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRedo}
                  className="h-8 text-xs"
                >
                  <Redo2 className="h-3 w-3" />
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                className="h-8 text-xs w-full"
              >
                {t('clear')}
              </Button>
            </div>

            <div className="flex-1" />

            {/* Save */}
            <Button onClick={handleSave} className="h-8 text-xs">
              <Save className="h-3 w-3 mr-1" />
              {t('save')}
            </Button>
          </div>

          {/* Canvas Area */}
          <div className="flex-1 relative bg-white rounded-lg border overflow-auto flex items-center justify-center p-4">
            <div className="relative inline-block" onClick={handleCanvasClick}>
              {/* Background image */}
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Background"
                className="block max-w-full max-h-[70vh] object-contain"
                style={{ display: 'block' }}
                crossOrigin="anonymous"
              />
              {/* Text canvas overlay */}
              {canvasSize.width > 0 && canvasSize.height > 0 && (
                <canvas
                  ref={textCanvasRef}
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              )}
              {/* Drawing canvas overlay */}
              {canvasSize.width > 0 && canvasSize.height > 0 && (
                <div 
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <ReactSketchCanvas
                    ref={canvasRef}
                    width={String(canvasSize.width)}
                    height={String(canvasSize.height)}
                    strokeWidth={strokeWidth}
                    strokeColor={strokeColor}
                    canvasColor="transparent"
                    style={{
                      border: 'none',
                      borderRadius: '0',
                      width: '100%',
                      height: '100%',
                      pointerEvents: currentTool === 'draw' ? 'auto' : 'none',
                    }}
                  />
                </div>
              )}
              {/* Shape canvas overlay - can be easily removed if not needed */}
              {canvasSize.width > 0 && canvasSize.height > 0 && (
                <canvas
                  ref={shapeCanvasRef}
                  className="absolute top-0 left-0"
                  width={Math.max(1, Math.floor(canvasSize.width))}
                  height={Math.max(1, Math.floor(canvasSize.height))}
                  style={{
                    width: '100%',
                    height: '100%',
                    pointerEvents: (currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star' || currentTool === 'select') ? 'auto' : 'none',
                    zIndex: 3,
                    cursor: (currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star') ? 'crosshair' : (currentTool === 'select' ? 'default' : 'default'),
                  }}
                  onMouseDown={(e) => {
                    if (!(currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star' || currentTool === 'select')) return;
                    // Try initialize manager on first interaction if not ready
                    if (!shapeMgrReadyRef.current && shapeCanvasRef.current && imageRef.current && shapeManagerRef.current) {
                      try {
                        shapeManagerRef.current.initialize(
                          shapeCanvasRef.current,
                          imageRef.current,
                          { width: canvasSize.width, height: canvasSize.height }
                        );
                        shapeMgrReadyRef.current = true;
                        console.log('[ImageEditor] Shape manager initialized (on demand)');
                      } catch {}
                    }
                    // If manager is ready, let it handle everything
                    if (shapeMgrReadyRef.current) return;
                    const canvasEl = e.target as HTMLCanvasElement;
                    const rect = canvasEl.getBoundingClientRect();
                    const scaleX = canvasEl.width / rect.width;
                    const scaleY = canvasEl.height / rect.height;
                    const x = (e.clientX - rect.left) * scaleX;
                    const y = (e.clientY - rect.top) * scaleY;
                    // eslint-disable-next-line no-console
                    console.log('[ShapeCanvas] mousedown', { x: Math.round(x), y: Math.round(y), tool: currentTool });
                    if (currentTool !== 'select') {
                      shapeManagerRef.current?.beginDraw(x, y);
                    }
                    // Fallback local drawing start
                    if (currentTool !== 'select') {
                      shapeDrawRef.current = { active: true, startX: x, startY: y, lastX: x, lastY: y, type: currentTool as any };
                      clearShapeOverlay();
                      drawTempShape(false);
                    }
                  }}
                  onMouseMove={(e) => {
                    if (!(currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star' || currentTool === 'select')) return;
                    if (shapeMgrReadyRef.current) return;
                    if (currentTool !== 'select' && e.buttons !== 1) return;
                    const canvasEl = e.target as HTMLCanvasElement;
                    const rect = canvasEl.getBoundingClientRect();
                    const scaleX = canvasEl.width / rect.width;
                    const scaleY = canvasEl.height / rect.height;
                    const x = (e.clientX - rect.left) * scaleX;
                    const y = (e.clientY - rect.top) * scaleY;
                    // eslint-disable-next-line no-console
                    console.log('[ShapeCanvas] drawing', { x: Math.round(x), y: Math.round(y) });
                    if (currentTool !== 'select') {
                      shapeManagerRef.current?.updateDraw(x, y);
                    }
                    // Fallback local drawing update
                    if (currentTool !== 'select' && shapeDrawRef.current.active) {
                      shapeDrawRef.current.lastX = x; shapeDrawRef.current.lastY = y;
                      clearShapeOverlay(); drawTempShape(false);
                    }
                  }}
                  onMouseUp={(e) => {
                    if (!(currentTool === 'rectangle' || currentTool === 'circle' || currentTool === 'star' || currentTool === 'select')) return;
                    if (shapeMgrReadyRef.current) return;
                    const canvasEl = e.target as HTMLCanvasElement;
                    const rect = canvasEl.getBoundingClientRect();
                    const scaleX = canvasEl.width / rect.width;
                    const scaleY = canvasEl.height / rect.height;
                    const x = (e.clientX - rect.left) * scaleX;
                    const y = (e.clientY - rect.top) * scaleY;
                    // eslint-disable-next-line no-console
                    console.log('[ShapeCanvas] mouseup', { x: Math.round(x), y: Math.round(y) });
                    if (currentTool !== 'select') {
                      shapeManagerRef.current?.endDraw();
                    }
                    // Fallback finalize and sync to manager if available
                    if (currentTool !== 'select' && shapeDrawRef.current.active) {
                      shapeDrawRef.current.lastX = x; shapeDrawRef.current.lastY = y;
                      const { startX, startY, lastX, lastY, type } = shapeDrawRef.current;
                      const bx = Math.min(startX, lastX);
                      const by = Math.min(startY, lastY);
                      const bw = Math.abs(lastX - startX);
                      const bh = Math.abs(lastY - startY);
                      clearShapeOverlay();
                      // Prefer syncing into manager so selection/resizing works
                      if (shapeManagerRef.current) {
                        shapeManagerRef.current.addShape(type as any, bx, by, bw, bh, strokeColor, strokeWidth);
                      } else {
                        drawTempShape(true);
                      }
                      shapeDrawRef.current.active = false;
                    }
                  }}
                />
              )}
              {/* Text elements for display (clickable to delete) */}
              {textElements.map(textEl => (
                <div
                  key={textEl.id}
                  className="absolute group cursor-pointer"
                  style={{
                    left: `${textEl.x}px`,
                    top: `${textEl.y}px`,
                    color: textEl.color,
                    fontSize: `${textEl.fontSize}px`,
                    fontFamily: 'Arial',
                    pointerEvents: currentTool === 'text' ? 'auto' : 'none',
                  }}
                  onClick={(e) => {
                    if (currentTool === 'text') {
                      e.stopPropagation();
                      handleDeleteText(textEl.id);
                    }
                  }}
                >
                  {textEl.text}
                  {currentTool === 'text' && (
                    <button
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteText(textEl.id);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Text Input Modal */}
        {isTextInputOpen && (
          <div 
            className="absolute z-50 bg-white border rounded-lg p-2 shadow-lg"
            style={{
              left: textPosition.x + 20,
              top: textPosition.y - 60,
              minWidth: '200px'
            }}
          >
            <div className="flex gap-2">
              <Input
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={t('textPlaceholder')}
                className="h-8 text-xs"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleTextSubmit();
                  } else if (e.key === 'Escape') {
                    setIsTextInputOpen(false);
                    setTextInput('');
                  }
                }}
              />
              <Button
                onClick={handleTextSubmit}
                size="sm"
                className="h-8 px-2"
              >
                {t('ok')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsTextInputOpen(false);
                  setTextInput('');
                }}
                size="sm"
                className="h-8 px-2"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
